/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";

import { setFilterValue, removeFilter } from "../../universeSlice";
import { getListsWithFilter } from "../../../lists/listSlice";
import { LIST_TYPES } from "../../../../common/metadata/ListTypes";
import { LIST_STATUSES } from "../../../../common/metadata/ListStatuses";

// LISTS filter (CONTAINED_IN / NOT_CONTAINED_IN) needs every saved/imported
// list as dropdown options, not the paginated page size the Lists admin
// table uses. A flat high limit rather than true pagination — simplest thing
// that works, revisit if an org's list count ever makes this payload heavy.
// Restricted to READY match/export lists, matching the legacy
// CompanyFilter/ContactFilter combobox — import/overlay lists were never
// offered here.
const ALL_LISTS_FILTER = {
    filter: {
        fields: ['id', 'name', 'type'],
        where: {
            and: [
                { tableRef: null },
                { status: LIST_STATUSES.READY.id },
                {
                    type: {
                        inq: [
                            LIST_TYPES.MATCH_COMPANY.id,
                            LIST_TYPES.MATCH_CONTACT.id,
                            LIST_TYPES.EXPORT_COMPANY.id,
                            LIST_TYPES.EXPORT_CONTACT.id,
                        ],
                    },
                },
            ],
        },
        order: ['name ASC'],
        limit: 10000,
    },
};

export const useUniverseFilter = (universeKey, searchText) => {
    const dispatch = useDispatch();

    // Selectors
    const metaDataState = useSelector(state => state.filtersMetaData);
    const listState = useSelector(state => state.list);
    const universeData = useSelector(state => state.universe.universes[universeKey]);

    const [filterState, setFilterState] = useState({});

    useEffect(() => {
        setFilterState(universeData?.entities || {});
    }, [universeData]);

    useEffect(() => {
        dispatch(getListsWithFilter(ALL_LISTS_FILTER));
    }, [dispatch]);

    const handleKeyValueChange = useCallback((key, value) => {
        setFilterState(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleRemoveFilter = useCallback((filterId) => {
        const idToRemove = filterId?.id || filterId;
        dispatch(removeFilter(idToRemove));
    }, [dispatch]);

    const handleSubmitFilter = useCallback((filterObj) => {
        dispatch(setFilterValue(filterObj));
    }, [dispatch]);

    const metaDataObj = useMemo(() => ({
        isLoading: metaDataState?.loading,
        ...metaDataState?.filtersMetaData,

        // OPERATING_STATUS, NAICS, and SIC previously remapped here because their
        // schemaProperty differs from the endpoint's option key. That's now
        // declared per-filter via `optionsSchemaProperty` in FilterConfig and
        // resolved in FilterComboBox.

        // TODO: Remove these hardcoded options once the metadata endpoint serves
        // company_email_status and company_fact_years_in_business_range; then
        // delete the entries and let the ...filtersMetaData spread supply them.

        // Email Status is a fixed enum (matches the company_email_status /
        // contact_email_status cube domain). Static, so it no longer depends on
        // the old getMetaData slice.
        ux_filter_profile_email_status: [
            { id: 'Catch All', display: 'Catch All' },
            { id: 'Disposable Email', display: 'Disposable Email' },
            { id: 'Not Available', display: 'Not Available' },
            { id: 'Role', display: 'Role' },
            { id: 'Verified', display: 'Verified' }
        ],

        company_fact_years_in_business_range: [
            {
                display: '0 - 2 Years',
                id: 'A:0-2'
            },
            {
                display: '3 - 5 Years',
                id: 'B:3-5'
            },
            {
                display: '6 - 10 Years',
                id: 'C:6-10'
            },
            {
                display: '11 - 25 Years',
                id: 'D:11-25'
            },
            {
                display: '26 - 50 Years',
                id: 'E:26-50'
            },
            {
                display: '51 - 100 Years',
                id: 'F:51-100'
            },
            {
                display: '101+ Years',
                id: 'G:101+'
            },
            {
                display: 'Year not specified',
                id: 'NULL'
            }
        ],

        // CONTAINED_IN / NOT_CONTAINED_IN (LISTS group) options: the org's own
        // saved/imported lists, not product metadata, so sourced from the list
        // slice rather than the filters-metadata endpoint.
        company_contractor_lists: (listState?.filterOptions ?? []).map(l => ({
            id: l.id,
            display: l.name,
            type: l.type,
        })),
    }), [metaDataState, listState]);

    return {
        commonProps: {
            filterState,
            metaDataObj,
            handleKeyValueChange,
            handleSubmitFilter,
            handleRemoveFilter,
            searchText,
            productSubscription: universeData.subscription
        }
    };
};